import { format } from "date-fns";
import { Check, PauseCircle, SkipForward, Repeat, XCircle, Play, CalendarClock, Truck } from "lucide-react";
import { formatGBP } from "@/lib/api";
import { cn } from "@/lib/utils";

const statusOf = (sub) => {
  if (sub.cancelsOn) return { label: `Cancels ${format(sub.cancelsOn, "d MMM")}`, cls: "bg-[#F7F5F0]/10 text-[#F7F5F0]" };
  if (sub.pastDue) return { label: "Payment failed", cls: "bg-red-500/15 text-red-300" };
  if (sub.pause) return { label: `Paused until ${format(sub.pause.until, "d MMM")}`, cls: "bg-[#D4A437]/15 text-[#F3E0A2]" };
  return { label: "Active", cls: "bg-[#FF6A00]/15 text-[#ff8a3d]" };
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-[#262626] bg-[#0b0b0b] p-4">
    <p className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#8a8a8a]"><Icon className="h-4 w-4" />{label}</p>
    <p className="mt-2 font-display text-lg font-semibold" data-testid={testid}>{value}</p>
  </div>
);

const Action = ({ icon: Icon, label, onClick, testid, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className="flex items-center gap-3 rounded-2xl border border-[#2E2E2E] px-4 py-3.5 text-sm font-semibold transition-[border-color,background-color] hover:border-[#FF6A00]/50 hover:bg-[#FF6A00]/5 disabled:opacity-40 disabled:pointer-events-none">
    <Icon className="h-5 w-5 text-[#FF6A00]" /> {label}
  </button>
);

export const SubscriptionCard = ({ sub, plan, pendingPlan, open, onResume, canSkip = true, canChangePlan = true }) => {
  const st = statusOf(sub);
  const fmt = (d) => format(d, "EEE d MMM yyyy");
  const ended = !!sub.cancelsOn;
  return (
    <section className="rounded-3xl border border-[#D4A437]/30 bg-[#12100e] p-6 sm:p-8" data-testid="subscription-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4A437]">Current subscription</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold" data-testid="subscription-plan-name">{plan?.name}{plan?.custom ? "" : " plan"}</h2>
        </div>
        <span className={cn("rounded-full px-3.5 py-1.5 text-sm font-semibold", st.cls)} data-testid="subscription-status">{st.label}</span>
      </div>
      <p className="mt-4 font-display text-4xl font-extrabold" data-testid="subscription-price">
        {plan && formatGBP(plan.price_pence)}<span className="text-base font-medium text-[#A1A1AA]"> /month</span>
      </p>
      <ul className="mt-5 grid gap-2 sm:grid-cols-2">
        {plan?.contents.map((c) => <li key={c} className="flex gap-2 text-sm text-[#F7F5F0]/85"><Check className="h-4 w-4 shrink-0 text-[#FF6A00]" />{c}</li>)}
      </ul>
      {pendingPlan && (
        <p className="mt-5 rounded-xl bg-[#FF6A00]/10 p-3 text-sm" data-testid="subscription-pending-change">
          Changing to <strong>{pendingPlan.name}</strong> ({formatGBP(pendingPlan.price_pence)}/month) from {fmt(sub.pause ? sub.pause.until : sub.nextBilling)}.
        </p>
      )}
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Stat icon={CalendarClock} label={sub.pause ? "Next payment (after pause)" : "Next billing date"} value={ended ? "No further payments" : fmt(sub.pause ? sub.pause.until : sub.nextBilling)} testid="subscription-next-billing" />
        <Stat icon={Truck} label="Next planned delivery" value={ended ? "None scheduled" : sub.pause ? `Resumes ${fmt(sub.pause.until)}` : sub.nextDelivery ? fmt(sub.nextDelivery) : "Date to be confirmed"} testid="subscription-next-delivery" />
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {sub.pause || ended ? (
          <Action icon={Play} label={ended ? "Keep my subscription" : "Resume now"} onClick={onResume} testid="action-resume" />
        ) : (
          <Action icon={PauseCircle} label="Pause subscription" onClick={() => open("pause")} testid="action-pause" />
        )}
        {canSkip && <Action icon={SkipForward} label={sub.skipped ? "Next delivery skipped" : "Skip next delivery"} onClick={() => open("skip")} disabled={sub.skipped || !!sub.pause || ended} testid="action-skip" />}
        {canChangePlan && <Action icon={Repeat} label="Change plan" onClick={() => open("plan")} disabled={ended} testid="action-change-plan" />}
        <Action icon={XCircle} label="Cancel subscription" onClick={() => open("cancel")} disabled={ended} testid="action-cancel" />
      </div>
    </section>
  );
};
